import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { GripVertical } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

const ResizeButton = styled.button<{
  $position: 'left' | 'bottom'
}>`
  position: absolute;
  cursor: ${({ $position }) =>
    $position === 'left' ? 'ew-resize' : 'ns-resize'};
  background-color: ${({ theme }) => theme.forecast.cardBackground};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  transition: all 0.15s ease;
  user-select: none;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  ${({ $position }) =>
    $position === 'left'
      ? `
    right: 0;
    top: 50%;
    transform: translate(50%, -50%);
    width: 1.5rem;
    height: 3rem;
  `
      : `
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 3rem;
    height: 1.5rem;
  `}
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
  }
  &:active {
    background-color: rgba(255, 255, 255, 0.15);
  }
  svg {
    color: ${({ theme }) => theme.forecast.text1};
    opacity: 0.9;
    transition: opacity 0.15s ease;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
    pointer-events: none;
  }
  &:hover svg {
    opacity: 1;
  }
`

const ResizeHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

interface ResizeHandleComponentProps {
  onWidthChange: (width: number | null) => void
  containerRef: React.RefObject<HTMLDivElement | null>
}

export const ResizeHandleComponent: React.FC<ResizeHandleComponentProps> = ({
  onWidthChange,
  containerRef,
}) => {
  const { isResizing, setIsResizing } = useScatterplotContext()
  const [hasMoved, setHasMoved] = useState(false)

  const startXRef = useRef<number>(0)
  const startWidthRef = useRef<number>(0)

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setHasMoved(false)
      startXRef.current = e.clientX
      if (containerRef.current) {
        startWidthRef.current = containerRef.current.offsetWidth
      }
      setIsResizing(true)
    },
    [containerRef, setIsResizing]
  )

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return

      // Set hasMoved flag if mouse has moved more than a few pixels
      if (!hasMoved && Math.abs(e.clientX - startXRef.current) > 3) {
        setHasMoved(true)
      }

      const deltaX = e.clientX - startXRef.current
      const newWidth = startWidthRef.current + deltaX
      const minWidth = 192 // 12rem in pixels (compact mode)

      if (newWidth >= minWidth) {
        onWidthChange(newWidth)
      }
    },
    [isResizing, hasMoved, onWidthChange]
  )

  const handleResizeEnd = useCallback(() => {
    if (isResizing && !hasMoved) {
      // This was a click, not a drag - trigger the toggle
      const narrowestWidth = 192 // 12rem in pixels (compact mode)
      const defaultWidth = 448 // 28rem in pixels

      // Toggle between narrowest and default
      if (containerRef.current) {
        const currentWidth = containerRef.current.offsetWidth
        if (currentWidth < (narrowestWidth + defaultWidth) / 2) {
          onWidthChange(defaultWidth)
        } else {
          onWidthChange(narrowestWidth)
        }
      }
    }

    setHasMoved(false)
    setIsResizing(false)
  }, [isResizing, hasMoved, containerRef, onWidthChange, setIsResizing])

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleResizeMove, handleResizeEnd])

  return (
    <ResizeButton
      $position='left'
      type='button'
      onMouseDown={handleResizeStart}>
      <ResizeHandle>
        <GripVertical width={'1rem'} height={'1rem'} />
      </ResizeHandle>
    </ResizeButton>
  )
}
